import type {
  AdditionalPropertiesSchema,
  DashboardColumnsConfig,
  DashboardLayoutSchemaVersion,
  SvgConstrainedPropertiesSchema,
  SvgViewBox,
  WidgetSlot
} from '@highimpact-frontend/api-types';

// -- type definitions --

export type {
  DashboardFreeLayoutProperties,
  DashboardLayoutProperties,
  DashboardSvgConstrainedLayoutProperties,
} from '@highimpact/dashboard-types';

// --- SvgConstrainedGridMetrics type ---

/** Metrics for an SVG-constrained grid layout. */
export type SvgConstrainedGridMetrics = {
  width: number;
  height: number;
  rowHeight: number;
};

// --- RankedWidgetSlot type ---

/** A configured slot with a stable index used for conflict resolution. */
export type RankedWidgetSlot = WidgetSlot & {
  rank: number;
};

// --- WidgetSlotPlacementRequest type ---

/** Describes where a widget wants to be placed before slots are resolved. */
export type WidgetSlotPlacementRequest = {
  elementId: number;
  requestedLayout: WidgetSlot;
};

// --- ResolvedWidgetSlotPlacement types ---

/** Base fields shared by every slot placement result. */
type ResolvedWidgetSlotPlacementBase = {
  elementId: number;
};

/** A widget was assigned to a configured slot. */
export type ResolvedWidgetSlotPlacementWithSlot =
  ResolvedWidgetSlotPlacementBase & {
    stateType: 'slot-found';
    assignedSlot: WidgetSlot;
    slotRank: number;
  };

/** No vacant slot was available for the widget. */
export type ResolvedWidgetSlotPlacementWithoutSlot =
  ResolvedWidgetSlotPlacementBase & {
    stateType: 'no-slot-found';
  };

/** Result of assigning one widget to a slot, or finding that none is available. */
export type ResolvedWidgetSlotPlacement =
  | ResolvedWidgetSlotPlacementWithSlot
  | ResolvedWidgetSlotPlacementWithoutSlot;

// --- ResolveSlotConflictsOptions type ---

/** Options for slot conflict resolution on an already partially filled dashboard. */
export type ResolveSlotConflictsOptions = {
  preoccupiedRanks?: ReadonlySet<number>;
};

// -- helper functions --

/** Checks whether a placement result includes an assigned slot. */
export const hasAssignedWidgetSlot = (
  placement: ResolvedWidgetSlotPlacement
): placement is ResolvedWidgetSlotPlacementWithSlot => {
  return placement.stateType === 'slot-found';
};

/**
 * Returns the schema version for dashboard layout settings.
 * Missing values are treated as version 1.0.0 for backward compatibility.
 */
export const resolveSchemaVersion = (
  properties: AdditionalPropertiesSchema
): DashboardLayoutSchemaVersion => {
  if (
    'schemaVersion' in properties &&
    properties.schemaVersion === '2.0.0'
  ) {
    return '2.0.0';
  }

  return '1.0.0';
};

/**
 * Checks whether the dashboard uses an SVG-constrained layout.
 */
export const isSvgConstrainedLayout = (
  properties: AdditionalPropertiesSchema
): properties is SvgConstrainedPropertiesSchema => {
  return (
    resolveSchemaVersion(properties) === '2.0.0' &&
    'layoutType' in properties &&
    properties.layoutType === 'svg-constrained' &&
    Array.isArray(properties.widgetSlot) &&
    typeof properties.svgData === 'string' &&
    properties.svgViewBox != null &&
    typeof properties.svgViewBox.width === 'number' &&
    typeof properties.svgViewBox.height === 'number'
  );
};

/**
 * Checks whether the dashboard uses a free grid layout.
 */
export const isFreeLayout = (
  properties: AdditionalPropertiesSchema
): boolean => {
  return !isSvgConstrainedLayout(properties);
};

/**
 * Decodes raw base64 SVG data into SVG markup.
 */
export const decodeSvgData = (base64SvgData: string): string => {
  // atob is deprecated, we use it if available
  if (typeof atob === 'function') {
    return atob(base64SvgData);
  }
  return Buffer.from(base64SvgData, 'base64').toString('utf-8');
};

/**
 * Ensures the SVG stretches to fill its container without preserving aspect ratio.
 * 
 * What should preserve the aspect ratio is its container, not the SVG itself.
 */
export const ensureSvgStretchesToFill = (svgMarkup: string): string => {
  // preserveAspectRatio present => set to none
  if (/preserveAspectRatio\s*=/.test(svgMarkup)) {
    return svgMarkup.replace(
      /preserveAspectRatio\s*=\s*["'][^"']*["']/,
      'preserveAspectRatio="none"'
    );
  }
  // preserveAspectRatio missing => add it, set to none
  return svgMarkup.replace('<svg', '<svg preserveAspectRatio="none"');
};

/**
 * Computes grid size from the available viewport height and view box aspect ratio.
 */
export const computeSvgConstrainedGridMetricsFromHeight = (
  availableHeight: number,
  viewBox: SvgViewBox
): SvgConstrainedGridMetrics => {
  if (availableHeight <= 0 || viewBox.width <= 0 || viewBox.height <= 0) {
    return { width: 0, height: 0, rowHeight: 0 };
  }

  const height = availableHeight;
  const width = height * (viewBox.width / viewBox.height);
  const rowHeight = height / viewBox.height;

  return { width, height, rowHeight };
};

/**
 * Builds the SVG markup used as a dashboard background from base64 payload data.
 */
export const buildSvgBackgroundMarkup = (base64SvgData: string): string => {
  return ensureSvgStretchesToFill(decodeSvgData(base64SvgData));
};

/**
 * Checks whether two widget layouts use the same position and size.
 */
export const widgetLayoutsAreEqual = (
  firstLayout: WidgetSlot,
  secondLayout: WidgetSlot
): boolean => {
  return (
    firstLayout.x === secondLayout.x &&
    firstLayout.y === secondLayout.y &&
    firstLayout.w === secondLayout.w &&
    firstLayout.h === secondLayout.h
  );
};

/**
 * Adds a stable rank to each configured slot based on its position in the array.
 */
export const toRankedWidgetSlots = (
  configuredSlots: WidgetSlot[]
): RankedWidgetSlot[] => {
  return configuredSlots.map((slot, rank) => ({ ...slot, rank }));
};

/**
 * Measures how far apart two layouts are using the distance between their center points.
 */
export const computeSlotCenterDistance = (
  firstLayout: WidgetSlot,
  secondLayout: WidgetSlot
): number => {
  const firstCenterX = firstLayout.x + firstLayout.w / 2;
  const firstCenterY = firstLayout.y + firstLayout.h / 2;
  const secondCenterX = secondLayout.x + secondLayout.w / 2;
  const secondCenterY = secondLayout.y + secondLayout.h / 2;
  const deltaX = firstCenterX - secondCenterX;
  const deltaY = firstCenterY - secondCenterY;
  return Math.sqrt(deltaX * deltaX + deltaY * deltaY);
};

/**
 * Finds the closest slot to a requested layout. When distances tie, the lower rank wins.
 */
export const findClosestRankedSlot = (
  requestedLayout: WidgetSlot,
  candidateSlots: RankedWidgetSlot[]
): RankedWidgetSlot | null => {
  if (candidateSlots.length === 0) {
    return null;
  }

  return candidateSlots.reduce((closestSlot, slot) => {
    const slotDistance = computeSlotCenterDistance(requestedLayout, slot);
    const closestDistance = computeSlotCenterDistance(
      requestedLayout,
      closestSlot
    );

    if (slotDistance < closestDistance) {
      return slot;
    }

    if (slotDistance === closestDistance && slot.rank < closestSlot.rank) {
      return slot;
    }

    return closestSlot;
  });
};

/**
 * Returns the rank of a configured slot that exactly matches a layout, or null when none match.
 */
export const findSlotRankForLayout = (
  configuredSlots: WidgetSlot[],
  layout: WidgetSlot
): number | null => {
  const matchIndex = configuredSlots.findIndex((slot) =>
    widgetLayoutsAreEqual(slot, layout)
  );
  return matchIndex === -1 ? null : matchIndex;
};

/**
 * Returns the ranks of slots already taken by existing widget layouts.
 */
export const getOccupiedSlotRanks = (
  configuredSlots: WidgetSlot[],
  existingLayouts: WidgetSlot[]
): Set<number> => {
  const occupiedRanks = new Set<number>();

  for (const layout of existingLayouts) {
    const rank = findSlotRankForLayout(configuredSlots, layout);
    if (rank !== null) {
      occupiedRanks.add(rank);
    }
  }

  return occupiedRanks;
};

const rankedSlotToWidgetSlot = (rankedSlot: RankedWidgetSlot): WidgetSlot => ({
  x: rankedSlot.x,
  y: rankedSlot.y,
  w: rankedSlot.w,
  h: rankedSlot.h,
});

const buildSlotFoundPlacement = (
  elementId: number,
  assignedRankedSlot: RankedWidgetSlot
): ResolvedWidgetSlotPlacementWithSlot => ({
  stateType: 'slot-found',
  elementId,
  assignedSlot: rankedSlotToWidgetSlot(assignedRankedSlot),
  slotRank: assignedRankedSlot.rank,
});

const buildNoSlotFoundPlacement = (
  elementId: number
): ResolvedWidgetSlotPlacementWithoutSlot => ({
  stateType: 'no-slot-found',
  elementId,
});

/**
 * Resolves slot conflicts for many widgets at once using a two-pass geometry-based algorithm.
 *
 * Steps:
 * - Pass 1 assigns each widget its preferred slot when that slot is still free.
 * - Pass 2 moves remaining widgets to the closest still-vacant slot.
 * - Widgets that cannot be placed are marked as not visible; callers should skip them.
 *
 * Notes:
 * - Lower elementId values win conflicts.
 * - Existing occupied ranks can be passed in so widgets already on the dashboard are never moved.
 */
export const resolveSlotConflictsByGeometry = (
  requests: WidgetSlotPlacementRequest[],
  configuredSlots: WidgetSlot[],
  options?: ResolveSlotConflictsOptions
): ResolvedWidgetSlotPlacement[] => {
  const rankedSlots = toRankedWidgetSlots(configuredSlots);
  const occupiedRanks = new Set(options?.preoccupiedRanks ?? []);
  const resolvedPlacements: ResolvedWidgetSlotPlacement[] = [];
  const unassignedRequests: WidgetSlotPlacementRequest[] = [];

  const sortedRequests = [...requests].sort(
    (first, second) => first.elementId - second.elementId
  );

  for (const request of sortedRequests) {
    const preferredSlot = findClosestRankedSlot(
      request.requestedLayout,
      rankedSlots
    );

    if (preferredSlot && !occupiedRanks.has(preferredSlot.rank)) {
      resolvedPlacements.push(
        buildSlotFoundPlacement(request.elementId, preferredSlot)
      );
      occupiedRanks.add(preferredSlot.rank);
      continue;
    }

    unassignedRequests.push(request);
  }

  for (const request of unassignedRequests) {
    const availableSlots = rankedSlots.filter(
      (slot) => !occupiedRanks.has(slot.rank)
    );
    const alternativeSlot = findClosestRankedSlot(
      request.requestedLayout,
      availableSlots
    );

    if (alternativeSlot) {
      resolvedPlacements.push(
        buildSlotFoundPlacement(request.elementId, alternativeSlot)
      );
      occupiedRanks.add(alternativeSlot.rank);
      continue;
    }

    resolvedPlacements.push(buildNoSlotFoundPlacement(request.elementId));
  }

  return resolvedPlacements;
};

/**
 * Resolves slot placement for a single new widget against slots already taken on the dashboard.
 */
export const resolveWidgetSlotPlacement = (
  request: WidgetSlotPlacementRequest,
  configuredSlots: WidgetSlot[],
  occupiedRanks: ReadonlySet<number>
): ResolvedWidgetSlotPlacement => {
  const [placement] = resolveSlotConflictsByGeometry(
    [request],
    configuredSlots,
    { preoccupiedRanks: occupiedRanks }
  );

  return placement ?? buildNoSlotFoundPlacement(request.elementId);
};

/**
 * Returns how many widget slots are still free on the dashboard.
 */
export const countAvailableWidgetSlots = (
  configuredSlots: WidgetSlot[],
  widgetCount: number
): number => {
  return Math.max(0, configuredSlots.length - widgetCount);
};

/**
 * Checks whether at least one widget slot is still free.
 */
export const hasAvailableWidgetSlots = (
  configuredSlots: WidgetSlot[],
  widgetCount: number
): boolean => {
  return countAvailableWidgetSlots(configuredSlots, widgetCount) > 0;
};
